import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { Users, FileText, Pencil, Download, FilePlus2, Trash2, Search, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const EMPTY_HR = {
  employee_id: '', designation: '', department: '', permanent_address: '',
  pan: '', uan: '', aadhar: '', bank_account: '', ifsc: '',
  emergency_contact_name: '', emergency_contact_number: '',
  marital_status: '', blood_group: '', location: '',
  laptop_make: '', laptop_model: '', laptop_serial: '',
  monthly_ctc: '', basic_percent: 40, hra_percent: 20,
  special_allowance: '', employer_pf: '', employee_pf: '',
  professional_tax: '', other_deductions: '', employment_type: 'full_time',
};

export default function HRDashboard() {
  const [employees, setEmployees] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingFor, setPendingFor] = useState(null);
  const [pendingChecks, setPendingChecks] = useState({}); // field -> bool
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [hrForm, setHrForm] = useState(EMPTY_HR);
  const [generating, setGenerating] = useState(null); // { user, doc_type }
  const [genCtx, setGenCtx] = useState({});
  const [docsFor, setDocsFor] = useState(null); // user object to view docs list
  const [docList, setDocList] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState(new Date().toISOString().slice(0, 7));
  const [bulkDays, setBulkDays] = useState(22);
  const [bulkLop, setBulkLop] = useState(0);
  const [bulkOverrides, setBulkOverrides] = useState({}); // { user_id: { days_worked, lop_days } }
  const [bulkComputed, setBulkComputed] = useState({}); // { user_id: { computed_days_worked, full_days, half_days, active_days } }
  const [bulkComputing, setBulkComputing] = useState(false);
  const [bulkSkip, setBulkSkip] = useState({}); // { user_id: true }
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const fetchAll = useCallback(() => {
    api.get('/hr/employees')
      .then(res => setEmployees(res.data || []))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load employees'));
    api.get('/hr/pending')
      .then(res => setPending(res.data || []))
      .catch(() => setPending([]));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEdit = (emp) => {
    setEditing(emp);
    // Prefill HR fields PLUS the personal-profile dates (which live on the
    // user doc root, not under hr_details).
    setHrForm({
      ...EMPTY_HR,
      ...(emp.hr_details || {}),
      date_of_birth: emp.date_of_birth || '',
      joining_date: emp.joining_date || '',
    });
  };

  const saveHR = async () => {
    try {
      const body = {};
      Object.entries(hrForm).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        if (['monthly_ctc','basic_percent','hra_percent','special_allowance','employer_pf','employee_pf','professional_tax','other_deductions'].includes(k)) {
          const n = parseFloat(v);
          if (!isNaN(n)) body[k] = n;
        } else {
          body[k] = v;
        }
      });
      await api.put(`/hr/employees/${editing.id}`, body);
      toast.success('HR details saved');
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const openGenerate = (emp, doc_type) => {
    setGenerating({ user: emp, doc_type });
    const today = new Date().toISOString().slice(0, 10);
    // Default payslip period to the PREVIOUS calendar month (typical case:
    // June's salary is generated in July). Super Admin can override for any period.
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const yyyymm = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
    // Leave days_worked blank at open — the effect below will fetch the
    // attendance-derived value for the chosen month and prefill it. If
    // attendance yields zero for that month, the SA can type a value.
    setGenCtx({ offer_date: today, payslip_month: yyyymm, payslip_days_worked: '', payslip_lop_days: 0 });
  };

  // Auto-prefill "Days worked" in the individual payslip generate dialog by
  // fetching the attendance-derived count for the selected month/user.
  useEffect(() => {
    if (!generating || generating.doc_type !== 'payslip') return;
    const uid = generating.user?.id;
    const month = genCtx.payslip_month;
    if (!uid || !month) return;
    let cancelled = false;
    api.get(`/hr/payslips/computed-days?month=${month}`)
      .then(res => {
        if (cancelled) return;
        const row = (res.data.employees || []).find(e => e.user_id === uid);
        if (!row) return;
        setGenCtx(prev => ({
          ...prev,
          _computed_days_worked: row.computed_days_worked,
          _computed_lop_days: row.computed_lop_days,
          _computed_full_days: row.full_days,
          _computed_half_days: row.half_days,
          _computed_active_days: row.active_days,
          // Only overwrite the input if the SA hasn't manually typed something
          // for this month yet (payslip_days_worked === '' after month switch).
          payslip_days_worked: (prev.payslip_days_worked === '' || prev.payslip_days_worked === undefined || prev.payslip_days_worked === 0)
            ? (row.computed_days_worked > 0 ? row.computed_days_worked : '')
            : prev.payslip_days_worked,
          payslip_lop_days: (prev.payslip_lop_days === 0 || prev.payslip_lop_days === '' || prev.payslip_lop_days === undefined)
            ? (row.computed_lop_days > 0 ? row.computed_lop_days : 0)
            : prev.payslip_lop_days,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [generating, genCtx.payslip_month]);

  const submitGenerate = async () => {
    try {
      // Drop UI-only underscore-prefixed helper keys (e.g. _computed_days_worked)
      // before posting — the backend model rejects unknown fields on some deployments.
      const cleanCtx = Object.fromEntries(Object.entries(genCtx).filter(([k]) => !k.startsWith('_')));
      const body = { user_id: generating.user.id, doc_type: generating.doc_type, ...cleanCtx };
      const res = await api.post('/hr/documents/generate', body);
      toast.success(`${res.data.title} generated (${res.data.size_kb} KB) — pushed to ${generating.user.full_name}'s login`);
      setGenerating(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate');
    }
  };

  const previewGenerate = async () => {
    try {
      const cleanCtx = Object.fromEntries(Object.entries(genCtx).filter(([k]) => !k.startsWith('_')));
      const body = { user_id: generating.user.id, doc_type: generating.doc_type, ...cleanCtx };
      const res = await api.post('/hr/documents/preview', body, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const openDocs = async (emp) => {
    setDocsFor(emp);
    try {
      const res = await api.get(`/hr/documents?user_id=${emp.id}`);
      setDocList(res.data || []);
    } catch {
      setDocList([]);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/hr/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${doc.title || doc.doc_type}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/hr/documents/${doc.id}`);
      setDocList(prev => prev.filter(d => d.id !== doc.id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const openBulk = () => {
    setBulkOverrides({});
    setBulkComputed({});
    setBulkSkip({});
    setBulkResult(null);
    setBulkOpen(true);
  };

  // Auto-fetch attendance-derived days worked per employee whenever the bulk
  // dialog is open and the month changes. Prefills the "Days" column so the
  // Super Admin doesn't have to enter every value manually. Rule: ≥9h/day = 1
  // full day; ≥4.5h/day = 0.5 day. Overrides still take precedence.
  useEffect(() => {
    if (!bulkOpen || !bulkMonth) return;
    let cancelled = false;
    setBulkComputing(true);
    api.get(`/hr/payslips/computed-days?month=${bulkMonth}`)
      .then(res => {
        if (cancelled) return;
        const map = {};
        const overridesFromComputed = {};
        (res.data.employees || []).forEach(e => {
          map[e.user_id] = e;
          // Prefill days_worked from attendance + approved paid/comp-off leaves.
          // Prefill lop_days from approved LOP leaves. Manual overrides still win.
          const ov = {};
          if (e.computed_days_worked > 0) ov.days_worked = String(e.computed_days_worked);
          if (e.computed_lop_days > 0) ov.lop_days = String(e.computed_lop_days);
          if (Object.keys(ov).length) overridesFromComputed[e.user_id] = ov;
        });
        setBulkComputed(map);
        // Merge with any existing manual overrides (existing wins).
        setBulkOverrides(prev => {
          const next = { ...overridesFromComputed };
          Object.entries(prev).forEach(([uid, ov]) => {
            next[uid] = { ...(next[uid] || {}), ...ov };
          });
          return next;
        });
      })
      .catch(() => { /* silent — table falls back to default_days */ })
      .finally(() => { if (!cancelled) setBulkComputing(false); });
    return () => { cancelled = true; };
  }, [bulkOpen, bulkMonth]);

  const openPendingReview = (emp) => {
    setPendingFor(emp);
    const seed = {};
    Object.keys(emp.hr_details_pending || {}).forEach(k => { seed[k] = true; });
    setPendingChecks(seed);
  };

  const approvePending = async () => {
    try {
      const approve_fields = Object.entries(pendingChecks).filter(([, v]) => v).map(([k]) => k);
      if (approve_fields.length === 0) {
        toast.error('Select at least one field to approve');
        return;
      }
      await api.post(`/hr/employees/${pendingFor.id}/approve`, { approve_fields });
      toast.success('Approved');
      setPendingFor(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const rejectPending = async () => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try {
      await api.post(`/hr/employees/${pendingFor.id}/reject`, { reason });
      toast.success('Rejected');
      setPendingFor(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const submitBulk = async () => {
    setBulkRunning(true);
    try {
      const skip_user_ids = Object.entries(bulkSkip).filter(([, v]) => v).map(([k]) => k);
      const overrides = {};
      Object.entries(bulkOverrides).forEach(([uid, ov]) => {
        if (ov && (ov.days_worked || ov.lop_days)) {
          overrides[uid] = {};
          if (ov.days_worked !== undefined && ov.days_worked !== '') {
            const n = parseFloat(ov.days_worked);
            if (!isNaN(n)) overrides[uid].days_worked = n;
          }
          if (ov.lop_days !== undefined && ov.lop_days !== '') {
            const n = parseFloat(ov.lop_days);
            if (!isNaN(n)) overrides[uid].lop_days = n;
          }
        }
      });
      const res = await api.post('/hr/payslips/bulk-generate', {
        month: bulkMonth,
        default_days_worked: parseFloat(bulkDays) || 22,
        default_lop_days: parseFloat(bulkLop) || 0,
        overrides,
        skip_user_ids,
      });
      setBulkResult(res.data);
      toast.success(`Generated ${res.data.generated_count} payslip(s). ${res.data.skipped_count} skipped.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk generation failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const filtered = employees.filter(e =>
    !search ||
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.mobile_number?.includes(search) ||
    e.hr_details?.employee_id?.toLowerCase().includes(search.toLowerCase()) ||
    e.hr_details?.designation?.toLowerCase().includes(search.toLowerCase())
  );

  const hrFieldsComplete = (emp) => {
    const h = emp.hr_details || {};
    return !!(h.employee_id && h.designation && h.pan && h.bank_account && h.monthly_ctc);
  };

  return (
    <div className="p-6 min-h-full bg-slate-50" data-testid="hr-dashboard">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-[#5B6FD6]" /> HR Master
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage employee master data, generate letters &amp; payslips. Documents are pushed to each employee&apos;s login automatically.</p>
        </div>
        <Button onClick={openBulk} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="bulk-payslip-btn">
          <Receipt className="h-4 w-4" /> Bulk payslip generation
        </Button>
      </div>

      <div className="mb-3 relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
        <Input
          placeholder="Search by name, mobile, employee ID or designation"
          value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9"
          data-testid="hr-search"
        />
      </div>

      {/* Pending approvals banner */}
      {pending.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 mb-3" data-testid="pending-approvals-banner">
          <CardContent className="p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1.5">
              {pending.length} pending HR change(s) awaiting your approval
            </p>
            <div className="flex flex-wrap gap-2">
              {pending.map(p => (
                <button
                  key={p.id}
                  onClick={() => openPendingReview(p)}
                  className="px-3 py-1.5 text-xs rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid={`pending-review-${p.id}`}
                >
                  {p.full_name} <span className="text-amber-500">({Object.keys(p.hr_details_pending || {}).length} fields)</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2.5">Employee</th>
                <th className="text-left px-4 py-2.5">Designation</th>
                <th className="text-left px-4 py-2.5">Emp ID</th>
                <th className="text-left px-4 py-2.5">DOB / DOJ</th>
                <th className="text-left px-4 py-2.5">Monthly CTC</th>
                <th className="text-left px-4 py-2.5">HR Master</th>
                <th className="text-left px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(emp => {
                const h = emp.hr_details || {};
                return (
                  <tr key={emp.id} className="hover:bg-slate-50" data-testid={`hr-row-${emp.id}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-800">{emp.full_name}</p>
                      <p className="text-xs text-slate-500">{emp.mobile_number}</p>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{h.designation || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-slate-700">{h.employee_id || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-slate-700 text-xs leading-tight" data-testid={`hr-dob-doj-${emp.id}`}>
                      <div>
                        <span className="text-slate-400">DOB:</span>{' '}
                        {emp.date_of_birth ? new Date(emp.date_of_birth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-slate-300">—</span>}
                      </div>
                      <div>
                        <span className="text-slate-400">DOJ:</span>{' '}
                        {emp.joining_date ? new Date(emp.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-slate-300">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{h.monthly_ctc ? `INR ${Number(h.monthly_ctc).toLocaleString('en-IN')}` : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {hrFieldsComplete(emp)
                        ? <Badge className="bg-emerald-100 text-emerald-700">Complete</Badge>
                        : <Badge className="bg-amber-100 text-amber-700">Incomplete</Badge>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openEdit(emp)} data-testid={`hr-edit-${emp.id}`} className="h-7 text-xs">
                          <Pencil className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Select onValueChange={(v) => openGenerate(emp, v)}>
                          <SelectTrigger className="w-[150px] h-7 text-xs" data-testid={`hr-gen-${emp.id}`}><SelectValue placeholder="Generate…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="offer_letter">Offer Letter</SelectItem>
                            <SelectItem value="experience_letter">Experience Letter</SelectItem>
                            <SelectItem value="internship_letter">Internship Letter</SelectItem>
                            <SelectItem value="payslip">Payslip</SelectItem>
                            <SelectItem value="laptop_issuance">Laptop Issuance Certificate</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={() => openDocs(emp)} data-testid={`hr-docs-${emp.id}`} className="h-7 text-xs">
                          <FileText className="h-3 w-3 mr-1" /> Docs
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">No employees found</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit HR Details Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="hr-edit-dialog">
          <DialogHeader>
            <DialogTitle>HR Master — {editing?.full_name}</DialogTitle>
            <DialogDescription>All fields are private and visible only to Super Admin.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee ID" value={hrForm.employee_id} onChange={(v) => setHrForm({ ...hrForm, employee_id: v })} testid="hr-emp-id" />
            <Field label="Designation" value={hrForm.designation} onChange={(v) => setHrForm({ ...hrForm, designation: v })} testid="hr-designation" />
            <Field label="Date of Birth" type="date" value={hrForm.date_of_birth || ''} onChange={(v) => setHrForm({ ...hrForm, date_of_birth: v })} testid="hr-dob" />
            <Field label="Joining Date" type="date" value={hrForm.joining_date || ''} onChange={(v) => setHrForm({ ...hrForm, joining_date: v })} testid="hr-doj" />
            <Field label="Department" value={hrForm.department} onChange={(v) => setHrForm({ ...hrForm, department: v })} testid="hr-department" />
            <Field label="Work Location" value={hrForm.location} onChange={(v) => setHrForm({ ...hrForm, location: v })} testid="hr-location" />
            <div className="col-span-2">
              <Field label="Permanent Address" value={hrForm.permanent_address} onChange={(v) => setHrForm({ ...hrForm, permanent_address: v })} testid="hr-address" />
            </div>
            <Field label="PAN" value={hrForm.pan} onChange={(v) => setHrForm({ ...hrForm, pan: v?.toUpperCase() })} testid="hr-pan" />
            <Field label="Aadhar" value={hrForm.aadhar} onChange={(v) => setHrForm({ ...hrForm, aadhar: v })} testid="hr-aadhar" />
            <Field label="UAN" value={hrForm.uan} onChange={(v) => setHrForm({ ...hrForm, uan: v })} testid="hr-uan" />
            <Field label="Bank A/c" value={hrForm.bank_account} onChange={(v) => setHrForm({ ...hrForm, bank_account: v })} testid="hr-bank" />
            <Field label="IFSC" value={hrForm.ifsc} onChange={(v) => setHrForm({ ...hrForm, ifsc: v?.toUpperCase() })} testid="hr-ifsc" />
            <Field label="Emergency Contact Name" value={hrForm.emergency_contact_name} onChange={(v) => setHrForm({ ...hrForm, emergency_contact_name: v })} testid="hr-ec-name" />
            <Field label="Emergency Contact Number" value={hrForm.emergency_contact_number} onChange={(v) => setHrForm({ ...hrForm, emergency_contact_number: v })} testid="hr-ec-num" />
            <div>
              <Label className="text-xs">Marital Status</Label>
              <Select value={hrForm.marital_status || 'unset'} onValueChange={(v) => setHrForm({ ...hrForm, marital_status: v === 'unset' ? '' : v })}>
                <SelectTrigger data-testid="hr-marital"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                  <SelectItem value="divorced">Divorced</SelectItem>
                  <SelectItem value="widowed">Widowed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Blood Group" value={hrForm.blood_group} onChange={(v) => setHrForm({ ...hrForm, blood_group: v })} testid="hr-blood" />
            <div>
              <Label className="text-xs">Employment Type</Label>
              <Select value={hrForm.employment_type || 'full_time'} onValueChange={(v) => setHrForm({ ...hrForm, employment_type: v })}>
                <SelectTrigger data-testid="hr-emptype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full-time</SelectItem>
                  <SelectItem value="intern">Intern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 mt-2 pt-2 border-t">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Company-issued laptop</p>
            </div>
            <Field label="Laptop Make" value={hrForm.laptop_make} onChange={(v) => setHrForm({ ...hrForm, laptop_make: v })} testid="hr-laptop-make" />
            <Field label="Laptop Model" value={hrForm.laptop_model} onChange={(v) => setHrForm({ ...hrForm, laptop_model: v })} testid="hr-laptop-model" />
            <div className="col-span-2">
              <Field label="Laptop Serial Number" value={hrForm.laptop_serial} onChange={(v) => setHrForm({ ...hrForm, laptop_serial: v })} testid="hr-laptop-serial" />
            </div>
            <div className="col-span-2 mt-2 pt-2 border-t">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Salary structure (monthly)</p>
            </div>
            <Field label="Monthly CTC" type="number" value={hrForm.monthly_ctc} onChange={(v) => setHrForm({ ...hrForm, monthly_ctc: v })} testid="hr-ctc" />
            <Field label="Special Allowance (override)" type="number" value={hrForm.special_allowance} onChange={(v) => setHrForm({ ...hrForm, special_allowance: v })} testid="hr-special" />
            <Field label="Basic %" type="number" value={hrForm.basic_percent} onChange={(v) => setHrForm({ ...hrForm, basic_percent: v })} testid="hr-basic" />
            <Field label="HRA %" type="number" value={hrForm.hra_percent} onChange={(v) => setHrForm({ ...hrForm, hra_percent: v })} testid="hr-hra" />
            {/* Jun 2026: PF / PT / Other Deductions inputs removed — will be re-added when statutory rollout begins. */}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveHR} className="bg-[#5B6FD6] hover:bg-[#4338CA]" data-testid="hr-save">Save HR Master</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Document Dialog */}
      <Dialog open={!!generating} onOpenChange={(o) => { if (!o) setGenerating(null); }}>
        <DialogContent className="max-w-md" data-testid="hr-gen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="h-5 w-5 text-[#5B6FD6]" />
              Generate {generating?.doc_type?.replaceAll('_', ' ')}
            </DialogTitle>
            <DialogDescription>For <b>{generating?.user?.full_name}</b>. Saved & pushed to their login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(generating?.doc_type === 'offer_letter' || generating?.doc_type === 'internship_letter') && (
              <>
                <Field label="Letter date" type="date" value={genCtx.offer_date} onChange={(v) => setGenCtx({ ...genCtx, offer_date: v })} testid="gen-offer-date" />
                <Field label="Joining date (override)" type="date" value={genCtx.joining_date_override || ''} onChange={(v) => setGenCtx({ ...genCtx, joining_date_override: v })} testid="gen-joining" />
                {generating?.doc_type === 'internship_letter' && (
                  <Field label="Internship end date" type="date" value={genCtx.last_working_day || ''} onChange={(v) => setGenCtx({ ...genCtx, last_working_day: v })} testid="gen-end" />
                )}
              </>
            )}
            {generating?.doc_type === 'experience_letter' && (
              <>
                <Field label="Letter date" type="date" value={genCtx.offer_date} onChange={(v) => setGenCtx({ ...genCtx, offer_date: v })} testid="gen-exp-date" />
                <Field label="Last working day" type="date" value={genCtx.last_working_day || ''} onChange={(v) => setGenCtx({ ...genCtx, last_working_day: v })} testid="gen-lwd" />
              </>
            )}
            {generating?.doc_type === 'laptop_issuance' && (
              <>
                <Field label="Issuance date" type="date" value={genCtx.offer_date} onChange={(v) => setGenCtx({ ...genCtx, offer_date: v })} testid="gen-laptop-date" />
                <Field
                  label="Laptop Make"
                  value={genCtx.laptop_make ?? (generating?.user?.hr_details?.laptop_make || '')}
                  onChange={(v) => setGenCtx({ ...genCtx, laptop_make: v })}
                  testid="gen-laptop-make"
                />
                <Field
                  label="Laptop Model"
                  value={genCtx.laptop_model ?? (generating?.user?.hr_details?.laptop_model || '')}
                  onChange={(v) => setGenCtx({ ...genCtx, laptop_model: v })}
                  testid="gen-laptop-model"
                />
                <Field
                  label="Serial Number"
                  value={genCtx.laptop_serial ?? (generating?.user?.hr_details?.laptop_serial || '')}
                  onChange={(v) => setGenCtx({ ...genCtx, laptop_serial: v })}
                  testid="gen-laptop-serial"
                />
                <p className="text-xs text-slate-500">
                  Fields pre-fill from HR Master. Edits here apply to this certificate only.
                </p>
              </>
            )}
            {generating?.doc_type === 'payslip' && (
              <>
                <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-md p-2 text-xs text-emerald-800">
                  <strong>Salary period:</strong> pick the month the salary is being <em>disbursed for</em> — not today&apos;s month.
                  E.g. a payslip generated in July for June&apos;s salary → pick <strong>June</strong>.
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Salary month (disbursed for)</label>
                  <input type="month" value={genCtx.payslip_month || ''}
                    onChange={(e) => setGenCtx({ ...genCtx, payslip_month: e.target.value, payslip_days_worked: '', _computed_days_worked: undefined })}
                    className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="gen-month" />
                </div>
                <Field label="Days worked" type="number" step="0.5" value={genCtx.payslip_days_worked ?? ''} onChange={(v) => setGenCtx({ ...genCtx, payslip_days_worked: v === '' ? '' : parseFloat(v) })} testid="gen-days" />
                <Field label="LOP days" type="number" step="0.5" value={genCtx.payslip_lop_days ?? 0} onChange={(v) => setGenCtx({ ...genCtx, payslip_lop_days: v === '' ? 0 : parseFloat(v) })} testid="gen-lop" />
                {genCtx._computed_days_worked !== undefined && (
                  <div className="col-span-2 text-xs text-slate-600" data-testid="gen-computed-hint">
                    Auto-calculated from attendance for <b>{genCtx.payslip_month}</b>:{' '}
                    <b className="text-slate-900">{genCtx._computed_days_worked}</b>{' '}
                    ({genCtx._computed_full_days}f + {genCtx._computed_half_days}h across {genCtx._computed_active_days} punched-in day(s)).
                    Edit above to override.
                  </div>
                )}
                {/* Variable line items — leave blank for none */}
                <Field label="Reimbursement (₹)" type="number" value={genCtx.reimbursement ?? ''}
                  onChange={(v) => setGenCtx({ ...genCtx, reimbursement: v === '' ? null : parseFloat(v) })}
                  testid="gen-reimbursement" />
                <Field label="Salary Advance — payout (₹)" type="number" value={genCtx.salary_advance_payout ?? ''}
                  onChange={(v) => setGenCtx({ ...genCtx, salary_advance_payout: v === '' ? null : parseFloat(v) })}
                  testid="gen-advance-payout" />
                <Field label="Leave deduction (₹)" type="number" value={genCtx.leave_deduction ?? ''}
                  onChange={(v) => setGenCtx({ ...genCtx, leave_deduction: v === '' ? null : parseFloat(v) })}
                  testid="gen-leave-deduction" />
                <Field label="Salary Advance — deduction (₹)" type="number" value={genCtx.salary_advance_deduction ?? ''}
                  onChange={(v) => setGenCtx({ ...genCtx, salary_advance_deduction: v === '' ? null : parseFloat(v) })}
                  testid="gen-advance-deduction" />
              </>
            )}
          </div>
          <DialogFooter className="flex justify-between gap-2">
            <Button variant="outline" onClick={previewGenerate} data-testid="gen-preview">Preview PDF</Button>
            <Button onClick={submitGenerate} className="bg-[#5B6FD6] hover:bg-[#4338CA]" data-testid="gen-submit">
              Generate & push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents list dialog */}
      <Dialog open={!!docsFor} onOpenChange={(o) => { if (!o) setDocsFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="hr-docs-dialog">
          <DialogHeader>
            <DialogTitle>Documents — {docsFor?.full_name}</DialogTitle>
            <DialogDescription>All letters & payslips generated for this employee.</DialogDescription>
          </DialogHeader>
          {docList.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No documents yet</p>
          ) : (
            <div className="divide-y divide-slate-100 -mx-6">
              {docList.map(d => (
                <div key={d.id} className="px-6 py-2.5 flex items-center gap-3" data-testid={`doc-row-${d.id}`}>
                  <FileText className="h-4 w-4 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{d.title}</p>
                    <p className="text-xs text-slate-500">Generated {new Date(d.created_at).toLocaleString()}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} className="h-7 text-xs" data-testid={`doc-download-${d.id}`}>
                    <Download className="h-3 w-3 mr-1" /> Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteDoc(d)} className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending approval review dialog */}
      <Dialog open={!!pendingFor} onOpenChange={(o) => { if (!o) setPendingFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="pending-review-dialog">
          <DialogHeader>
            <DialogTitle>Review pending changes — {pendingFor?.full_name}</DialogTitle>
            <DialogDescription>Approve individual fields or reject the whole submission.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {pendingFor && Object.entries(pendingFor.hr_details_pending || {}).map(([k, v]) => {
              const current = (pendingFor.hr_details || {})[k];
              return (
                <div key={k} className="border rounded-md p-2.5 flex items-start gap-3" data-testid={`pending-field-${k}`}>
                  <input
                    type="checkbox"
                    checked={!!pendingChecks[k]}
                    onChange={(e) => setPendingChecks({ ...pendingChecks, [k]: e.target.checked })}
                    className="mt-1"
                    data-testid={`pending-check-${k}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{k.replaceAll('_', ' ')}</p>
                    <p className="text-xs text-slate-400 line-through">{current || '—'}</p>
                    <p className="text-sm text-slate-800 font-medium">→ {String(v)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="flex justify-between gap-2">
            <Button variant="outline" onClick={rejectPending} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="pending-reject">
              Reject all
            </Button>
            <Button onClick={approvePending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pending-approve">
              Approve selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Payslip Generation Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) setBulkOpen(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-payslip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-emerald-600" />
              Bulk Payslip Generation
            </DialogTitle>
            <DialogDescription>
              Generates a payslip for every active employee with a CTC configured, in one click.
              Employees without HR Master setup are skipped automatically.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <Field label="Salary month (disbursed for)" type="month" value={bulkMonth} onChange={setBulkMonth} testid="bulk-month" />
                <Field label="Default days worked" type="number" value={bulkDays} onChange={setBulkDays} testid="bulk-days" />
                <Field label="Default LOP days" type="number" value={bulkLop} onChange={setBulkLop} testid="bulk-lop" />
              </div>

              <div className="text-xs bg-emerald-50 border border-emerald-100 rounded px-2.5 py-1.5 mb-2 text-emerald-800" data-testid="bulk-auto-hint">
                <b>Auto-calculated from attendance:</b> ≥ 9h/day (incl. lunch) counts as 1 full day, ≥ 4.5h/day as half day.
                {bulkComputing && <span className="ml-2 text-slate-500">Recomputing…</span>}
              </div>

              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-2 mb-1.5">
                Per-employee overrides (optional)
              </p>
              <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-slate-600">
                      <th className="text-left px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">CTC</th>
                      <th className="text-left px-3 py-2 w-28">Attendance</th>
                      <th className="text-left px-3 py-2 w-24">Days</th>
                      <th className="text-left px-3 py-2 w-24">LOP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {employees.map(emp => {
                      const ctc = emp.hr_details?.monthly_ctc;
                      const skip = !!bulkSkip[emp.id];
                      const computed = bulkComputed[emp.id];
                      return (
                        <tr key={emp.id} className={skip ? 'opacity-50' : ''} data-testid={`bulk-row-${emp.id}`}>
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox"
                              checked={!skip}
                              onChange={(e) => setBulkSkip({ ...bulkSkip, [emp.id]: !e.target.checked })}
                              disabled={!ctc}
                              data-testid={`bulk-include-${emp.id}`}
                            />
                          </td>
                          <td className="px-3 py-1.5 text-slate-700">{emp.full_name}</td>
                          <td className="px-3 py-1.5 text-slate-500">
                            {ctc ? `INR ${Number(ctc).toLocaleString('en-IN')}` : <span className="text-amber-600">No CTC</span>}
                          </td>
                          <td className="px-3 py-1.5 text-slate-600" data-testid={`bulk-attendance-${emp.id}`}>
                            {computed ? (
                              computed.active_days > 0 ? (
                                <span title={`${computed.full_days} full + ${computed.half_days} half over ${computed.active_days} punched-in day(s)`}>
                                  <b className="text-slate-800">{computed.computed_days_worked}</b>
                                  <span className="text-slate-400 ml-1">({computed.full_days}f + {computed.half_days}h)</span>
                                </span>
                              ) : <span className="text-slate-300">no data</span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-1.5">
                            <input
                              type="number"
                              step="0.5"
                              placeholder={computed?.computed_days_worked > 0 ? String(computed.computed_days_worked) : String(bulkDays)}
                              className="w-16 border border-slate-200 rounded px-1.5 py-0.5 text-xs"
                              disabled={!ctc || skip}
                              value={bulkOverrides[emp.id]?.days_worked ?? ''}
                              onChange={(e) => setBulkOverrides({ ...bulkOverrides, [emp.id]: { ...(bulkOverrides[emp.id] || {}), days_worked: e.target.value } })}
                              data-testid={`bulk-days-${emp.id}`}
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <input
                              type="number"
                              step="0.5"
                              placeholder={String(bulkLop)}
                              className="w-16 border border-slate-200 rounded px-1.5 py-0.5 text-xs"
                              disabled={!ctc || skip}
                              value={bulkOverrides[emp.id]?.lop_days ?? ''}
                              onChange={(e) => setBulkOverrides({ ...bulkOverrides, [emp.id]: { ...(bulkOverrides[emp.id] || {}), lop_days: e.target.value } })}
                              data-testid={`bulk-lop-${emp.id}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {bulkResult && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Generated" value={bulkResult.generated_count} color="bg-emerald-50 text-emerald-700" />
                <Stat label="Skipped" value={bulkResult.skipped_count} color="bg-amber-50 text-amber-700" />
                <Stat label="Failed" value={bulkResult.failed_count} color="bg-red-50 text-red-700" />
              </div>
              {bulkResult.skipped?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Skipped</p>
                  <ul className="text-xs text-slate-600 space-y-0.5 max-h-32 overflow-y-auto">
                    {bulkResult.skipped.map(s => <li key={s.id} data-testid={`bulk-skipped-${s.id}`}>• {s.name} — <span className="text-slate-400">{s.reason}</span></li>)}
                  </ul>
                </div>
              )}
              {bulkResult.failed?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-1">Failed</p>
                  <ul className="text-xs text-red-600 space-y-0.5 max-h-32 overflow-y-auto">
                    {bulkResult.failed.map(s => <li key={s.id}>• {s.name} — {s.error}</li>)}
                  </ul>
                </div>
              )}
              {bulkResult.generated_count > 0 && (
                <p className="text-xs text-emerald-700 bg-emerald-50 rounded p-2">
                  Payslips pushed to each employee&apos;s &quot;My Documents&quot;. They can download anytime.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkRunning}>Cancel</Button>
                <Button onClick={submitBulk} disabled={bulkRunning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-submit">
                  {bulkRunning ? 'Generating…' : 'Generate all'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setBulkOpen(false); fetchAll(); }} className="bg-[#5B6FD6] hover:bg-[#4338CA]" data-testid="bulk-close">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div className={`rounded-md px-3 py-2 text-center ${color}`}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', testid, ...rest }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />
  </div>
);
